import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CardsList from '@components/CardsList'
import { POneEvent } from '@modules/auth/hooks/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import { ChatType } from '@modules/chat/types'
import { useChatContext } from '@modules/chat/useChatContext'
import { AttendeeItem } from '@modules/profile/types'
import { Delegate } from '@modules/search/types'
import ChatIcon from '@mui/icons-material/Chat'
import { Box, Button } from '@mui/material'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { useElementResize } from '@shared/hooks/useElementResize'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'

import {
  AttendeesEventName,
  AttendeesExpandedList,
  AttendeesListWrapper,
  AttendeesTitle,
  AttendeesWrapper,
  ContactIcon,
  PARTICIPANT_CARD_MIN_WIDTH,
  ParticipantAltInfo,
  ParticipantCard,
  ParticipantInfo,
  ParticipantItemBox,
  ParticipantName,
} from './index.styles'

interface ConferenceAttendeesProps {
  attendeesList: AttendeeItem[]
  total: number
  companyId: number
  onLoadMore?: () => void
  eventData: Nullable<POneEvent>
  smallViewMode?: boolean
}

const getProfileUrl = (profileId: number) => `/plus/profile/${profileId}/delegate`

export const useMappedAttendeesProfiles = (profiles: AttendeeItem[]) => {
  const attendeesCardsList = useMemo(
    () =>
      profiles.map(profile => {
        const {
          firstName = '',
          lastName = '',
          jobTitle = '',
          profilePicture = '',
          id,
          isHidden = false,
          globalCompanyId,
        } = profile
        return {
          raw: profile,
          globalCompanyId,
          cardLinkUrl: getProfileUrl(id),
          imageSrc: profilePicture,
          imageAltInfo: `${firstName.charAt(0)}${lastName.charAt(0)}`,
          isDisabled: isHidden,
          cardInfo: {
            cardInfoTitle: `${firstName} ${lastName}`,
            cardInfoDescription: jobTitle,
          },
        }
      }),
    [profiles],
  )

  return attendeesCardsList
}

/** THIS FILE REQUIRES REFACTORING, SPLITTED IT FROM A BIGGER PEACE ONLY SO WE CAN RE-USE */
const Attendees = (props: ConferenceAttendeesProps) => {
  const { attendeesList, total, smallViewMode, eventData, companyId, onLoadMore } = props

  const { user } = useAuthContext()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)
  const { hasAccessToChat, canSeeAttendees } = useFeatureFlags()
  const { openChatModal } = useChatContext()
  const attendeesListRef = useRef<Nullable<HTMLDivElement>>(null)
  const { t } = useTranslation()
  const { elementWidth } = useElementResize(attendeesListRef)

  const [isCollapsedView, setCollapsedView] = useState<boolean>(true)

  const attendeesCardsList = useMappedAttendeesProfiles(attendeesList)

  useEffect(() => {
    if (!attendeesCardsList.length) {
      return
    }
    const containerWidth = elementWidth || 0
    const isCollapsedMode = Math.floor(containerWidth / total) <= PARTICIPANT_CARD_MIN_WIDTH

    setCollapsedView(isCollapsedMode)
  }, [attendeesListRef.current, elementWidth, attendeesCardsList])

  const renderParticipantInfoBox = (profileData: AttendeeItem, profileId: number, isDisabled: boolean) => (
    <ParticipantInfo>
      <ParticipantName variant="h3">
        <Link href={isDisabled ? getProfileUrl(profileId) : undefined} underline="none">
          {profileData.title} {profileData.firstName} {profileData.lastName}
        </Link>
      </ParticipantName>
      {profileData.jobTitle && <Typography variant="body1">{profileData.jobTitle}</Typography>}
    </ParticipantInfo>
  )

  const renderAvatarBox = ({
    profileData,
    profileId,
    collapsedMode = false,
  }: {
    profileData: AttendeeItem
    profileId: number
    collapsedMode?: boolean
    isDisabled?: boolean
  }) => {
    const profileName = `${profileData.title} ${profileData.firstName} ${profileData.lastName}`
    return (
      <ParticipantItemBox collapsedMode={collapsedMode} $isDisabled={profileData.isHidden}>
        <Link href={getProfileUrl(profileId)} underline="none">
          {profileData.profilePicture ? (
            <img alt={profileName} src={profileData.profilePicture} />
          ) : (
            <ParticipantAltInfo>
              {profileData.firstName.charAt(0)}
              {profileData.lastName.charAt(0)}
            </ParticipantAltInfo>
          )}
        </Link>
      </ParticipantItemBox>
    )
  }

  const renderCollapsedList = () => {
    if (!attendeesList) {
      return null
    }
    return (
      <CardsList
        cardsItems={attendeesCardsList}
        onLoadMore={attendeesList.length < total ? onLoadMore : undefined}
        totalCardsAmount={total}
        renderActionButton={(item, isTooltipView) =>
          hasAccessToChat &&
          !isEcommerce && (
            <Box>
              {item.id !== user?.id && (
                <Box mt="6px">
                  <ContactedBubble hasContacted={item.hasContacted}>
                    {isTooltipView ? (
                      <ContactIcon
                        onClick={() =>
                          openChatModal({
                            companyId,
                            delegates: [item] as unknown as Delegate[],
                            chatType: ChatType.DIRECT,
                          })
                        }
                      >
                        <ChatIcon fontSize="small" />
                      </ContactIcon>
                    ) : (
                      <Button
                        variant="contained"
                        data-testid="contact_btn"
                        onClick={() =>
                          openChatModal({
                            companyId,
                            delegates: [item] as unknown as Delegate[],
                            chatType: ChatType.DIRECT,
                          })
                        }
                      >
                        {t(translationKeys.CONTACT_BUTTON)}
                      </Button>
                    )}
                  </ContactedBubble>
                </Box>
              )}
            </Box>
          )
        }
      />
    )
  }

  const renderExpandedList = () => {
    if (!attendeesList?.length) {
      return null
    }

    return attendeesList.map((item: AttendeeItem) => {
      return (
        <ParticipantCard key={item.id} inPopoverList={false}>
          {renderAvatarBox({ profileData: item, profileId: item.id })}

          <Box display="flex" flexDirection="column">
            {renderParticipantInfoBox(item, item.id, item.isHidden)}
            {item.id !== user?.id && hasAccessToChat && !isEcommerce && (
              <Box mt="10px">
                <ContactedBubble hasContacted={item.hasContacted}>
                  <Button
                    variant="contained"
                    data-testid="contact_btn"
                    onClick={() =>
                      openChatModal({ delegates: [item] as unknown as Delegate[], companyId, chatType: ChatType.DIRECT })
                    }
                  >
                    {t(translationKeys.CONTACT_BUTTON)}
                  </Button>
                </ContactedBubble>
              </Box>
            )}
          </Box>
        </ParticipantCard>
      )
    })
  }

  if (!canSeeAttendees) return null

  return attendeesList?.length ? (
    <AttendeesWrapper smallViewMode={smallViewMode} data-testid="attendees-wrapper">
      <AttendeesTitle variant="h3">{t(translationKeys.ATTENDEES_TITLE)}</AttendeesTitle>
      {eventData && <AttendeesEventName>{eventData.name}</AttendeesEventName>}
      <AttendeesListWrapper ref={attendeesListRef} data-testid="attendees-list">
        {isCollapsedView ? (
          renderCollapsedList()
        ) : (
          <AttendeesExpandedList data-testid="attendees-expanded-list">{renderExpandedList()}</AttendeesExpandedList>
        )}
      </AttendeesListWrapper>
    </AttendeesWrapper>
  ) : null
}

export default memo(Attendees)
